import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { useI18n } from "../i18n";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chatbot() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t.chat.greeting }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    // Reset greeting on lang change
    setMessages((m) =>
      m.length === 0 ? m : [{ role: "assistant", content: t.chat.greeting }, ...m.slice(1)]
    );
  }, [lang]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        message: msg,
        session_id: sessionId,
        lang,
      });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: t.chat.error }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((s) => !s)}
        className="fixed bottom-24 right-6 z-50 h-14 w-14 rounded-full bg-[#00B3B3] text-white flex items-center justify-center hover:scale-110 transition-transform animate-pulse-glow"
        aria-label="Open chat"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>

      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-44 right-6 z-50 w-[min(92vw,400px)] h-[min(65vh,560px)] bg-[#0c0c0c] border border-white/15 rounded-sm shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#0a0a0a] to-[#111] flex items-center justify-between">
            <div>
              <div className="font-heading font-bold text-white text-sm flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#39FF14] inline-block" />
                {t.chat.title}
              </div>
              <div className="font-mono text-[10px] text-white/40 tracking-widest mt-0.5">
                {t.chat.sub}
              </div>
            </div>
            <button
              data-testid="chatbot-close"
              onClick={() => setOpen(false)}
              className="text-white/60 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed rounded-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#00B3B3] text-white font-medium"
                      : "bg-[#161616] text-white/90 border border-white/10"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#161616] border border-white/10 px-3 py-2 text-sm text-white/60 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> {t.chat.thinking}
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-white/10 p-3 flex items-center gap-2 bg-[#080808]">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.chat.placeholder}
              className="flex-1 bg-[#111] border border-white/10 text-white text-sm px-3 py-2 rounded-sm outline-none focus:border-[#00B3B3] placeholder:text-white/30"
            />
            <button
              type="submit"
              data-testid="chatbot-send"
              disabled={loading || !input.trim()}
              className="bg-[#00B3B3] text-white p-2 rounded-sm disabled:opacity-40 hover:bg-[#00CCCC] transition-colors"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
